<script lang="ts">
  import type { PublicVesselSummary } from "../../shared/contracts";
  import { vesselDisplayName } from "../format";
  import { vesselFacesBrickell } from "../vessel-heading";
  import UnknownVesselBadge from "./UnknownVesselBadge.svelte";

  export let vessel: PublicVesselSummary;
  export let point: { x: number; y: number };
  export let ordinal: number;
  export let selected = false;
  export let groupSelected = false;
  export let linked = false;
  export let compact = false;
  export let symbolOffset: { x: number; y: number } = { x: 0, y: 0 };
  export let relationship: string | null = null;
  export let onselect: (vessel: PublicVesselSummary, trigger: HTMLElement) => void;

  let failed = false;
  $: name = vesselDisplayName(vessel);
  // Asset masters face downriver/screen-right; a hull travelling toward
  // Brickell is mirrored using the same facing evidence as the 3D scene.
  $: mirrored = vesselFacesBrickell(vessel);
  $: phaseLabel = vessel.bridgePhase === "waiting"
    ? "waiting at Brickell"
    : vessel.bridgePhase === "clearing"
      ? "clearing Brickell"
      : "on the Brickell route";
  $: markerWidth = compact ? 64 : 84;
  $: markerHeight = compact ? 46 : 58;
  $: hasSymbolOffset = symbolOffset.x !== 0 || symbolOffset.y !== 0;
</script>

<g
  class:vessel-marker--selected={selected}
  class:vessel-marker--group-selected={groupSelected}
  class:vessel-marker--linked={linked}
  class:vessel-marker--compact={compact}
  class="vessel-marker"
  data-phase={vessel.bridgePhase}
  data-symbol-offset={`${symbolOffset.x},${symbolOffset.y}`}
  transform={`translate(${point.x} ${point.y})`}
>
  {#if selected || groupSelected}
    <circle
      class:vessel-marker__selection--related={groupSelected && !selected}
      class="vessel-marker__selection"
      cx={symbolOffset.x}
      cy={symbolOffset.y}
      r={compact ? 27 : 34}
      aria-hidden="true"
    />
  {/if}
  {#if hasSymbolOffset}
    <g class="vessel-marker__anchor" aria-hidden="true">
      <line class="vessel-marker__leader-casing" x1="0" y1="0" x2={symbolOffset.x} y2={symbolOffset.y} />
      <line class="vessel-marker__leader-line" x1="0" y1="0" x2={symbolOffset.x} y2={symbolOffset.y} />
      <circle class="vessel-marker__anchor-dot" r="4" />
    </g>
  {/if}
  <foreignObject
    x={symbolOffset.x - markerWidth / 2}
    y={symbolOffset.y - markerHeight / 2}
    width={markerWidth}
    height={markerHeight}
  >
    <button
      class="vessel-marker__button"
      type="button"
      aria-label={`View vessel ${ordinal}, ${name}, ${phaseLabel}${relationship ? `, ${relationship}` : ""}`}
      onclick={(event) => onselect(vessel, event.currentTarget)}
    >
      {#if !failed}
        <img
          class:mirrored
          src={`/assets/vessels/${vessel.type}-compact.webp`}
          alt=""
          onerror={() => (failed = true)}
        />
      {:else}
        <svg class:mirrored viewBox="0 0 120 58" aria-hidden="true">
          <path class="vessel-fallback__hull" d="M5 35H98L116 27L104 48H21Z" />
          <path class="vessel-fallback__cabin" d="M35 34V15H76L91 34Z" />
          <path class="vessel-fallback__window" d="M43 20H55V29H43ZM60 20H73L81 29H60Z" />
          <path class="vessel-fallback__mast" d="M60 15V3M60 5L82 13" />
        </svg>
      {/if}
      {#if vessel.type === "generic"}<UnknownVesselBadge />{/if}
    </button>
  </foreignObject>
  <g
    class="vessel-marker__index"
    transform={`translate(${symbolOffset.x + (compact ? -24 : -31)} ${symbolOffset.y + (compact ? -18 : -23)})`}
    aria-hidden="true"
  >
    <rect x="-11" y="-10" width="22" height="20" />
    <text y="4" text-anchor="middle">{ordinal.toString().padStart(2, "0")}</text>
  </g>
</g>
